import { createClient } from "@/lib/supabase/client"
import { buildBackendRequestError } from "@/lib/backend/error-details.js"
import type { WorldBootstrap } from "@/lib/world/world-types"
import { getCharacterPlatformBackendUrl } from "./server"

const SESSION_WAIT_STEP_MS = 150
const BOOTSTRAP_SESSION_WAIT_MS = 2500
const SESSION_REQUEST_TIMEOUT_MS = 1200

export type WorldBootstrapResponse = WorldBootstrap & {
  openclaw_connected?: boolean
  life_sim_catalog?: Record<string, unknown> | null
}

export type WorldTaskSnapshotEvent = {
  type: "task_update"
  agent_id: string
  tasks?: Record<string, unknown> | null
  world?: Record<string, unknown> | null
  life_sim?: Record<string, unknown> | null
}

export type PresenceEvent = {
  type: "openclaw.presence"
  connected: boolean
  lease_id: string | null
}

type ParsedResponseDetail = {
  message: string
  phase?: string
  warnings?: string[]
  agent_id?: string
}

async function backendFetch(path: string, init?: RequestInit) {
  const url = new URL(path, getCharacterPlatformBackendUrl())
  return fetch(url, {
    ...init,
    cache: "no-store",
  })
}

async function appRouteFetch(path: string, init?: RequestInit) {
  return fetch(path, {
    ...init,
    cache: "no-store",
  })
}

async function readJsonResponse<T>(
  response: Response,
  errorPrefix: string
): Promise<T> {
  if (!response.ok) {
    const body = await response.text()
    const detail = parseResponseDetail(body)
    throw new Error(
      `${errorPrefix}: ${response.status} ${detail.message || response.statusText}`.trim()
    )
  }

  return response.json() as Promise<T>
}

function parseResponseDetail(body: string): ParsedResponseDetail {
  if (!body) {
    return { message: "" }
  }

  try {
    const parsed = JSON.parse(body) as { detail?: unknown }
    if (typeof parsed?.detail === "string") {
      return { message: parsed.detail }
    }
    if (parsed?.detail && typeof parsed.detail === "object") {
      const detail = parsed.detail as {
        message?: unknown
        phase?: unknown
        warnings?: unknown
        agent_id?: unknown
      }
      return {
        message:
          typeof detail.message === "string"
            ? detail.message
            : JSON.stringify(detail),
        phase: typeof detail.phase === "string" ? detail.phase : undefined,
        warnings: Array.isArray(detail.warnings)
          ? detail.warnings.filter((value): value is string => typeof value === "string")
          : undefined,
        agent_id: typeof detail.agent_id === "string" ? detail.agent_id : undefined,
      }
    }
  } catch {}

  return { message: body }
}

async function getSessionSnapshot(supabase: ReturnType<typeof createClient>) {
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  try {
    return await Promise.race([
      supabase.auth.getSession(),
      new Promise<{ data: { session: null }; error: null }>((resolve) => {
        timeoutId = setTimeout(() => {
          resolve({
            data: { session: null },
            error: null,
          })
        }, SESSION_REQUEST_TIMEOUT_MS)
      }),
    ])
  } finally {
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
    }
  }
}

async function getAccessToken({
  waitForSessionMs = 0,
}: {
  waitForSessionMs?: number
} = {}) {
  const supabase = createClient()
  const deadline = Date.now() + waitForSessionMs

  while (true) {
    const { data } = await getSessionSnapshot(supabase)
    const accessToken = data?.session?.access_token ?? null
    if (accessToken) {
      return accessToken
    }

    if (Date.now() >= deadline) {
      return null
    }

    await new Promise((resolve) => {
      setTimeout(resolve, SESSION_WAIT_STEP_MS)
    })
  }
}

const inflightBootstrap = new Map<string, Promise<WorldBootstrapResponse>>()

export async function fetchWorldBootstrap({
  accessToken: initialAccessToken = null,
  waitForSessionMs = BOOTSTRAP_SESSION_WAIT_MS,
}: {
  accessToken?: string | null
  waitForSessionMs?: number
} = {}): Promise<WorldBootstrapResponse> {
  const accessToken =
    initialAccessToken ??
    (await getAccessToken({
      waitForSessionMs,
    }))

  const dedupeKey = accessToken ?? "__anon__"
  const existing = inflightBootstrap.get(dedupeKey)
  if (existing) return existing

  const run = (async (): Promise<WorldBootstrapResponse> => {
    // Keep the initial workspace bootstrap on same-origin app routes so
    // signed-in browser sessions do not depend on direct cross-origin fetches.
    return readJsonResponse<WorldBootstrapResponse>(
      await appRouteFetch("/api/world/bootstrap", { method: "GET" }),
      "World bootstrap failed"
    )
  })()

  inflightBootstrap.set(dedupeKey, run)
  try {
    return await run
  } finally {
    if (inflightBootstrap.get(dedupeKey) === run) {
      inflightBootstrap.delete(dedupeKey)
    }
  }
}

export async function importWorldAgents() {
  await getAccessToken({
    waitForSessionMs: BOOTSTRAP_SESSION_WAIT_MS,
  })

  return readJsonResponse(
    await appRouteFetch("/api/world/import", { method: "POST" }),
    "World import failed"
  )
}

export type CreateWorldAgentRequest = {
  name: string
  role?: string
  home_city?: string
  home_coords?: { lat: number; lng: number }
  home_bbox?: [number, number, number, number]
  chat_app_model?: string
  personal_places?: Record<string, unknown> | null
  character_config_input?: {
    personality_prompt?: string
    backstory_prompt?: string
    quirks_and_mannerisms?: string
    beliefs_and_intents?: string
  }
  directives_input?: {
    autonomy_level: number
    mission?: string
    behavioral?: string
    constraints?: string
  }
}

export type CreateWorldAgentResponse = {
  agent_id: string
  name: string
  generated_files: {
    identity_md: string
    soul_md: string
    user_md: string
    agents_md: string
  }
  recommended_skills: string[]
  installed_skills: string[]
  profile: Record<string, unknown>
  world: Record<string, unknown>
  warnings: string[]
}

export class WorldAgentCreateError extends Error {
  status?: number
  detail?: string
  phase?: string
  warnings?: string[]
  agentId?: string
}

export async function createWorldAgent(
  payload: CreateWorldAgentRequest
): Promise<CreateWorldAgentResponse> {
  const accessToken = await getAccessToken()
  if (!accessToken) {
    throw new Error("Missing character-platform session")
  }

  const res = await backendFetch("/v1/world/agents/create", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    const body = await res.text()
    const detail = parseResponseDetail(body)
    const error = new WorldAgentCreateError(
      `World agent creation failed: ${res.status} ${detail.message || ""}`.trim()
    )
    error.status = res.status
    error.detail = detail.message
    error.phase = detail.phase
    error.warnings = detail.warnings
    error.agentId = detail.agent_id
    throw error
  }

  return res.json()
}

export async function patchWorldAgent(agentId: string, payload: Record<string, unknown>) {
  const accessToken = await getAccessToken()
  if (!accessToken) {
    throw new Error("Missing character-platform session")
  }
  const res = await backendFetch(`/v1/world/agent/${encodeURIComponent(agentId)}`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const body = await res.text()
    const detail = parseResponseDetail(body)
    const error = new Error(
      `World agent update failed: ${res.status} ${detail.message || ""}`.trim()
    ) as Error & {
      detail?: string
      responseBody?: string
      status?: number
    }
    error.status = res.status
    error.detail = detail.message
    error.responseBody = body
    throw error
  }
  return res.json()
}

export type AgentPersonaFilesResponse = {
  files: {
    identity_md: string | null
    soul_md: string | null
    user_md: string | null
    agents_md: string | null
  }
  filenames: Record<string, string>
  files_hash: string | null
  character_config_source: string | null
  present: boolean
  regenerated?: boolean
  character_config?: Record<string, unknown> | null
}

export async function getAgentPersonaFiles(
  agentId: string
): Promise<AgentPersonaFilesResponse> {
  const accessToken = await getAccessToken()
  if (!accessToken) {
    throw new Error("Missing character-platform session")
  }
  const res = await backendFetch(
    `/v1/world/agent/${encodeURIComponent(agentId)}/persona-files`,
    {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  )
  if (!res.ok) {
    const body = await res.text()
    const detail = parseResponseDetail(body)
    const error = new Error(
      `Persona files fetch failed: ${res.status} ${detail.message || ""}`.trim()
    ) as Error & { status?: number; detail?: string }
    error.status = res.status
    error.detail = detail.message
    throw error
  }
  return res.json()
}

export async function putAgentPersonaFiles(
  agentId: string,
  files: Partial<Record<"identity_md" | "soul_md" | "user_md" | "agents_md", string>>
): Promise<AgentPersonaFilesResponse> {
  const accessToken = await getAccessToken()
  if (!accessToken) {
    throw new Error("Missing character-platform session")
  }
  const res = await backendFetch(
    `/v1/world/agent/${encodeURIComponent(agentId)}/persona-files`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ files }),
    }
  )
  if (!res.ok) {
    const body = await res.text()
    const detail = parseResponseDetail(body)
    const error = new Error(
      `Persona files save failed: ${res.status} ${detail.message || ""}`.trim()
    ) as Error & { status?: number; detail?: string }
    error.status = res.status
    error.detail = detail.message
    throw error
  }
  return res.json()
}

export async function postWorldTaskEvent(
  agentId: string,
  payload: Record<string, unknown>
) {
  const accessToken = await getAccessToken()
  if (!accessToken) {
    throw new Error("Missing character-platform session")
  }
  const res = await backendFetch(
    `/v1/world/agent/${encodeURIComponent(agentId)}/tasks/events`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  )
  if (!res.ok) {
    throw await buildBackendRequestError(res, "World task event failed.")
  }
  return res.json()
}

export async function streamWorldTaskEvents({
  onEvent,
  onPresence,
  signal,
  waitForSessionMs = BOOTSTRAP_SESSION_WAIT_MS,
}: {
  onEvent: (event: WorldTaskSnapshotEvent) => void
  onPresence?: (event: PresenceEvent) => void
  signal?: AbortSignal
  waitForSessionMs?: number
}) {
  const accessToken = await getAccessToken({ waitForSessionMs })
  if (!accessToken) {
    throw new Error("Missing character-platform session")
  }

  const response = await backendFetch("/v1/world/events/stream", {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    signal,
  })

  if (!response.ok) {
    const body = await response.text()
    const detail = parseResponseDetail(body)
    throw new Error(
      `World task stream failed: ${response.status} ${detail.message || ""}`.trim()
    )
  }

  if (!response.body) {
    throw new Error("World task stream failed: missing response body")
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  let currentEvent = ""

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) {
        if (signal?.aborted) {
          return
        }
        throw new Error("World task stream closed unexpectedly")
      }

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split("\n")
      buffer = lines.pop() ?? ""

      for (const rawLine of lines) {
        const line = rawLine.trimEnd()
        if (!line) {
          currentEvent = ""
          continue
        }
        if (line.startsWith(":")) {
          continue
        }
        if (line.startsWith("event:")) {
          currentEvent = line.slice(6).trim()
          continue
        }
        if (!line.startsWith("data:")) {
          continue
        }

        let payload: unknown
        try {
          payload = JSON.parse(line.slice(5).trim())
        } catch {
          continue
        }

        if (currentEvent === "task_update") {
          onEvent(payload as WorldTaskSnapshotEvent)
        } else if (currentEvent === "openclaw.presence" && onPresence) {
          onPresence(payload as PresenceEvent)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}
